import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Stack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Input,
  Select,
  NumberInput,
  NumberInputField,
  HStack,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  TagCloseButton,
  Button,
  Textarea,
  Checkbox,
  Text,
  useToast,
} from "@chakra-ui/react";
import { ModalShell } from "../../../shared/ui";
import { Plus } from "../../../shared/icons";
import { isPlanoBloqueado } from "../../../shared/api/planoBloqueado";
import { apiErrorMessage } from "../../../shared/api/apiErrorMessage";
import { formatBRL } from "../../../shared/lib/currency";
import { hojeISO } from "../../../shared/lib/date";
import { plantaoSchema, OBSERVACAO_MAX } from "../schemas/plantaoSchema";
import { useCreatePlantaoMutation } from "../hooks/useCreatePlantaoMutation";
import { useCreatePlantoesBatchMutation } from "../hooks/useCreatePlantoesBatchMutation";
import { useConflictGuard } from "../hooks/useConflictGuard";
import { describeBatchResult } from "../utils/batchSummary";
import { ShiftConflictWarning, ShiftConflictFooter } from "./ShiftConflictWarning";
import { useVinculosQuery } from "../../vinculos/hooks/useVinculosQuery";
import { sortFavoritesFirst, singleFavorite } from "../../vinculos/utils/sortFavoritesFirst";
import { suggestedShiftValue, hasWeekendRate, datesHaveMixedRates } from "../../vinculos/utils/suggestedShiftValue";
import VinculoColorDot from "../../vinculos/components/VinculoColorDot";

// Mesmo limite do backend (POST /plantoes/lote): dois meses de plantões diários.
const MAX_DATAS = 62;

function defaultValues(vinculoInicial) {
  return { vinculoId: vinculoInicial ?? "", data: hojeISO(), horas: 12, valor: "", horaInicio: "", observacao: "", importante: false };
}

// "2026-11-10" -> "10/11/2026"
function formatIsoDate(iso) {
  const [ano, mes, dia] = iso.split("-");
  return `${dia}/${mes}/${ano}`;
}

// `dataInicial` ("yyyy-MM-dd") vem do clique num dia do Calendário; sem ela, hoje.
// `plantaoBase` (uma linha de plantão) é a ação "Duplicar": o formulário abre com vínculo,
// horas, valor, hora, observação e destaque do plantão, e a data vazia para escolher.
export default function RegistrarPlantaoModal({ isOpen, onClose, vinculoInicial, dataInicial, plantaoBase = null }) {
  const toast = useToast();
  const mutation = useCreatePlantaoMutation();
  const batchMutation = useCreatePlantoesBatchMutation();
  const conflictGuard = useConflictGuard();
  const { data: vinculosData } = useVinculosQuery();
  const vinculos = useMemo(() => sortFavoritesFirst(vinculosData ?? []), [vinculosData]);
  // Datas além da principal ("Data do plantão"): com alguma, o cadastro vai pelo lote.
  const [outrasDatas, setOutrasDatas] = useState([]);
  const [adicionandoData, setAdicionandoData] = useState(false);
  const [novaData, setNovaData] = useState("");
  const [erroNovaData, setErroNovaData] = useState("");
  // O valor vem sugerido pelo vínculo para a data (fim de semana incluído) até o médico
  // digitar outro: a partir daí trocar a data não mexe mais no valor.
  const [valorEditado, setValorEditado] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitted },
  } = useForm({
    resolver: zodResolver(plantaoSchema),
    defaultValues: defaultValues(vinculoInicial),
  });

  const vinculoSelecionadoId = watch("vinculoId");
  const dataPrincipal = watch("data");
  const valorAtual = watch("valor");
  const vinculoSelecionado = vinculos.find((v) => String(v.id) === String(vinculoSelecionadoId));
  // Ao duplicar, o valor copiado do plantão de origem é uma escolha, não uma sugestão.
  const duplicandoMesmoVinculo = Boolean(plantaoBase) && String(plantaoBase.vinculo_id) === String(vinculoSelecionadoId);
  const valorManual = valorEditado || duplicandoMesmoVinculo;

  // O modal fica montado entre aberturas (o form não remonta), então a data escolhida no
  // calendário é aplicada a cada abertura em vez de só nos defaultValues.
  useEffect(() => {
    if (isOpen && dataInicial) setValue("data", dataInicial);
  }, [isOpen, dataInicial, setValue]);

  useEffect(() => {
    if (!isOpen || !plantaoBase) return;
    reset({
      vinculoId: String(plantaoBase.vinculo_id),
      data: "",
      horas: plantaoBase.horas,
      valor: plantaoBase.valor,
      horaInicio: plantaoBase.hora_inicio ?? "",
      observacao: plantaoBase.observacao ?? "",
      importante: Boolean(plantaoBase.importante),
    });
  }, [isOpen, plantaoBase, reset]);

  // As opções do select chegam depois do vínculo já escolhido (vinculoInicial, duplicar):
  // reaplica a escolha para o select não ficar mostrando o placeholder.
  useEffect(() => {
    const atual = getValues("vinculoId");
    if (vinculos.length > 0 && atual) setValue("vinculoId", String(atual));
  }, [vinculos, getValues, setValue]);

  // Com exatamente um vínculo favorito, o cadastro "do zero" já vem com ele escolhido. Não
  // vale quando o modal abriu com um vínculo (vinculoInicial) ou duplicando um plantão, nem
  // sobrescreve uma escolha já feita.
  useEffect(() => {
    if (!isOpen || plantaoBase || vinculoInicial || getValues("vinculoId")) return;
    const favorito = singleFavorite(vinculos);
    if (favorito) setValue("vinculoId", String(favorito.id));
  }, [isOpen, plantaoBase, vinculoInicial, vinculos, getValues, setValue]);

  // Escolher o vínculo pré-preenche duração e hora de início padrão (e volta o valor para o
  // sugerido); todos continuam editáveis para exceções em um plantão específico. Ao
  // duplicar, o vínculo do plantão de origem não sobrescreve os valores copiados dele.
  useEffect(() => {
    if (duplicandoMesmoVinculo || !vinculoSelecionado) return;
    setValorEditado(false);
    setValue("horas", vinculoSelecionado.horas_plantao ?? 12);
    if (vinculoSelecionado.hora_inicio_padrao) setValue("horaInicio", vinculoSelecionado.hora_inicio_padrao);
  }, [vinculoSelecionado, duplicandoMesmoVinculo, setValue]);

  // Valor sugerido para a data principal (ver suggestedShiftValue), enquanto o médico não
  // digitar outro.
  useEffect(() => {
    if (valorManual || !vinculoSelecionado) return;
    setValue("valor", suggestedShiftValue(vinculoSelecionado, dataPrincipal));
  }, [vinculoSelecionado, dataPrincipal, valorManual, setValue]);

  const limparDatasExtras = () => {
    setOutrasDatas([]);
    setAdicionandoData(false);
    setNovaData("");
    setErroNovaData("");
  };

  const fechar = () => {
    reset(defaultValues(vinculoInicial));
    setValorEditado(false);
    limparDatasExtras();
    conflictGuard.dismiss();
    onClose();
  };

  const adicionarData = () => {
    if (!novaData) {
      setErroNovaData("Escolha a data");
      return;
    }
    if (novaData === dataPrincipal || outrasDatas.includes(novaData)) {
      setErroNovaData("Essa data já está na lista");
      return;
    }
    setOutrasDatas([...outrasDatas, novaData].sort());
    setNovaData("");
    setErroNovaData("");
  };

  const avisarErro = (error) => {
    // 402: quem fala com o médico é o PlanoBloqueadoModal (com o botão de assinar).
    if (isPlanoBloqueado(error)) return;
    toast({
      title: "Não foi possível registrar o plantão",
      description: apiErrorMessage(error),
      status: "error",
      duration: 5000,
      isClosable: true,
    });
  };

  const salvar = (values, datas) => {
    if (datas.length === 1) {
      mutation.mutate(
        { ...values, data: datas[0] },
        {
          onSuccess: (plantao) => {
            toast({ title: `Plantão registrado · ${formatBRL(plantao.valor)}`, status: "success", duration: 4000 });
            fechar();
          },
          onError: avisarErro,
        }
      );
      return;
    }
    batchMutation.mutate(
      { ...values, datas },
      {
        onSuccess: (resultado) => {
          toast({ title: describeBatchResult(resultado), status: "success", duration: 5000 });
          fechar();
        },
        onError: avisarErro,
      }
    );
  };

  // As extras incluem a digitada em "Outra data" mas ainda não confirmada com "Adicionar":
  // descartá-la em silêncio perderia um plantão que o médico acha que registrou.
  const datasExtras = [...new Set([...outrasDatas, adicionandoData ? novaData : ""])].filter(Boolean).sort();

  // Lote que mistura dias úteis e fim de semana com o valor só sugerido vai SEM valor: o
  // backend precifica cada data pelo seu dia (domain.Vinculo.ValorSugerido).
  const precoPorData = (datas) => !valorManual && datesHaveMixedRates(vinculoSelecionado, datas);

  const onSubmit = (values) => {
    const datas = [...new Set([values.data, ...datasExtras])];
    const payload = precoPorData(datas) ? { ...values, valor: undefined } : values;
    conflictGuard.guard({ datas, horaInicio: values.horaInicio, horas: values.horas }, () => salvar(payload, datas));
  };

  const isSubmitting = mutation.isPending || batchMutation.isPending || conflictGuard.isChecking;
  // Conta datas distintas: a principal pode ter sido trocada para uma das extras.
  const totalDatas = 1 + datasExtras.filter((data) => data !== dataPrincipal).length;
  const datasDoCadastro = [...new Set([dataPrincipal, ...datasExtras])].filter(Boolean);

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={fechar}
      title={plantaoBase ? "Duplicar plantão" : "Registrar plantão"}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={totalDatas > 1 ? `Registrar ${totalDatas} plantões` : "Registrar"}
      isSubmitting={isSubmitting}
      footer={
        conflictGuard.hasConflicts ? (
          <ShiftConflictFooter onConfirm={conflictGuard.confirm} onBack={conflictGuard.dismiss} isSubmitting={isSubmitting} />
        ) : undefined
      }
    >
      {conflictGuard.hasConflicts ? (
        <ShiftConflictWarning conflitos={conflictGuard.conflitos} />
      ) : (
        <Stack spacing={4}>
          <FormControl isInvalid={Boolean(errors.vinculoId)}>
            <FormLabel>Vínculo</FormLabel>
            {/* <option> nativo não aceita marcador colorido: o ponto na cor do vínculo
                escolhido fica ao lado do select, e os favoritos vêm primeiro com ★. */}
            <HStack spacing={2}>
              {vinculoSelecionado ? <VinculoColorDot cor={vinculoSelecionado.cor} size="12px" /> : null}
              <Select placeholder="Escolha o vínculo" {...register("vinculoId")}>
                {vinculos.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.favorito ? `★ ${v.nome}` : v.nome}
                  </option>
                ))}
              </Select>
            </HStack>
            <FormErrorMessage>{errors.vinculoId?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={Boolean(errors.data)}>
            <FormLabel>Data do plantão</FormLabel>
            <Input type="date" {...register("data")} />
            <FormErrorMessage>{errors.data?.message}</FormErrorMessage>
          </FormControl>

          {outrasDatas.length > 0 ? (
            <Wrap spacing={2} aria-label="Outras datas">
              {outrasDatas.map((data) => (
                <WrapItem key={data}>
                  <Tag size="md" borderRadius="full" variant="subtle" colorScheme="brand">
                    <TagLabel>{formatIsoDate(data)}</TagLabel>
                    <TagCloseButton
                      aria-label={`Remover ${formatIsoDate(data)}`}
                      onClick={() => setOutrasDatas(outrasDatas.filter((d) => d !== data))}
                    />
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          ) : null}

          {adicionandoData ? (
            <FormControl isInvalid={Boolean(erroNovaData)}>
              <FormLabel>Outra data</FormLabel>
              <HStack align="flex-start">
                <Input type="date" value={novaData} onChange={(e) => setNovaData(e.target.value)} />
                <Button variant="outline" onClick={adicionarData}>
                  Adicionar
                </Button>
              </HStack>
              <FormErrorMessage>{erroNovaData}</FormErrorMessage>
            </FormControl>
          ) : null}

          {totalDatas < MAX_DATAS ? (
            <Button
              variant="link"
              size="sm"
              alignSelf="flex-start"
              leftIcon={<Plus size={14} />}
              onClick={() => setAdicionandoData(true)}
            >
              Adicionar outra data
            </Button>
          ) : (
            <Text fontSize="sm" color="app-muted-fg">
              Limite de {MAX_DATAS} datas por cadastro.
            </Text>
          )}

          <HStack spacing={4} align="flex-start">
            <FormControl isInvalid={Boolean(errors.horaInicio)}>
              <FormLabel>Hora de início</FormLabel>
              <Input type="time" {...register("horaInicio")} />
              <FormErrorMessage>{errors.horaInicio?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={Boolean(errors.horas)}>
              <FormLabel>Horas</FormLabel>
              <NumberInput min={1} max={48}>
                <NumberInputField {...register("horas")} />
              </NumberInput>
              <FormErrorMessage>{errors.horas?.message}</FormErrorMessage>
            </FormControl>
          </HStack>

          <FormControl isInvalid={Boolean(errors.valor)}>
            <FormLabel>Valor (R$)</FormLabel>
            {/* Controlado pelo formulário: o valor sugerido muda com a data, e um input só
                ligado por ref perderia o valor a cada nova renderização do NumberInput. */}
            <NumberInput
              min={0}
              precision={2}
              value={valorAtual ?? ""}
              onChange={(texto) => {
                // o blur só reformata ("1500" -> "1500.00"); não é o médico digitando
                if (Number(texto) !== Number(getValues("valor"))) setValorEditado(true);
                setValue("valor", texto, { shouldValidate: isSubmitted });
              }}
            >
              <NumberInputField name="valor" />
            </NumberInput>
            {hasWeekendRate(vinculoSelecionado) ? (
              <FormHelperText>
                Sábados e domingos: {formatBRL(vinculoSelecionado.valor_fim_de_semana)}
                {precoPorData(datasDoCadastro) ? " · cada data entra com o valor do seu dia" : ""}
              </FormHelperText>
            ) : null}
            <FormErrorMessage>{errors.valor?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={Boolean(errors.observacao)}>
            <FormLabel>Observação</FormLabel>
            <Textarea rows={2} maxLength={OBSERVACAO_MAX} placeholder="Ex.: troca com a Dra. Ana" {...register("observacao")} />
            <FormHelperText>Opcional · aparece no calendário.</FormHelperText>
            <FormErrorMessage>{errors.observacao?.message}</FormErrorMessage>
          </FormControl>

          <Checkbox {...register("importante")}>Marcar como importante</Checkbox>
        </Stack>
      )}
    </ModalShell>
  );
}
